import React from "react";

/** Recursively extract the error cause chain. */
function collectCauseChain(error: unknown): unknown[] {
  const chain: unknown[] = [];
  let current: unknown = error;
  const seen = new Set<unknown>();

  while (current && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    if (current instanceof Error) {
      current = current.cause;
    } else if (
      typeof current === "object" &&
      current !== null &&
      "cause" in current
    ) {
      current = (current as Record<string, unknown>).cause;
    } else {
      break;
    }
  }

  return chain;
}

/** Format a single error entry for display. */
function formatErrorEntry(entry: unknown, depth: number): React.ReactNode {
  if (entry instanceof Error) {
    return (
      <div key={depth} style={{ marginLeft: depth * 16 }}>
        <strong>
          {depth > 0 ? `Caused by: ` : ""}
          {entry.name}: {entry.message}
        </strong>
        {entry.stack && (
          <pre className="mt-1 mb-2 text-xs wrap-break-word whitespace-pre-wrap text-slate-400">
            {entry.stack}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div key={depth} style={{ marginLeft: depth * 16 }}>
      <strong>{depth > 0 ? "Caused by: " : ""}</strong>
      <pre className="text-xs wrap-break-word whitespace-pre-wrap text-slate-400">
        {typeof entry === "object"
          ? JSON.stringify(entry, null, 2)
          : String(entry)}
      </pre>
    </div>
  );
}

interface ErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

/**
 * Full-screen error display with nested cause chain.
 * Used by ErrorBoundary as its fallback UI.
 */
export function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
  const chain = collectCauseChain(error);

  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center bg-slate-900 p-8 text-slate-50"
    >
      <div className="w-full max-w-3xl">
        <h1 className="mb-2 text-2xl font-bold text-red-500">
          Something went wrong
        </h1>
        <p className="mb-6 text-slate-400">
          An unexpected error occurred. The details below may help diagnose the
          issue.
        </p>

        <div className="mb-6 max-h-[60vh] overflow-auto rounded-lg border border-slate-700 bg-slate-800 p-4">
          {chain.map((entry, i) => formatErrorEntry(entry, i))}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="cursor-pointer rounded-md bg-blue-500 px-5 py-2 font-semibold text-white hover:bg-blue-600"
          >
            Try again
          </button>
          <button
            onClick={() => {
              window.location.href = "/signin";
            }}
            className="cursor-pointer rounded-md border border-slate-600 bg-transparent px-5 py-2 font-semibold text-slate-400 hover:border-slate-500"
          >
            Go to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
